import { useState, useEffect } from 'react';
import { services as svcApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CodeBlock = ({ code }) => (
  <pre className="bg-background border border-border/50 rounded-lg p-4 overflow-x-auto text-xs font-mono leading-relaxed">
    <code>{code}</code>
  </pre>
);

const endpoints = [
  { category: 'ML Models', items: [
    { method: 'POST', path: '/api/v1/embeddings/embed', service: 'bge_m3 / minilm', desc: 'Generate dense vector embeddings (1024d BGE-M3 or 384d MiniLM)',
      body: `{ "texts": ["What is ML?", "Deep learning basics"], "model": "bge_m3", "return_sparse": false, "return_colbert": false }`,
      response: `{ "model": "bge_m3", "data": { "dense": [[0.023, -0.045, ...], ...], "sparse": [...], "colbert": [...] }, "count": 2 }` },
    { method: 'POST', path: '/api/v1/embeddings/rerank', service: 'bge_reranker', desc: 'Rerank passages by relevance to a query using cross-encoder',
      body: `{ "query": "neural networks", "passages": ["Neural nets use layers...", "Trees grow..."], "top_k": 2 }`,
      response: `{ "model": "bge_reranker_v2_m3", "results": [{ "index": 0, "passage": "...", "score": 0.94 }] }` },
    { method: 'POST', path: '/api/v1/embeddings/nlp', service: 'spacy', desc: 'NLP: NER, POS tagging, dependency parsing, sentence splitting',
      body: `{ "text": "Apple Inc. released iPhone 15 in Cupertino." }`,
      response: `{ "model": "en_core_web_sm", "data": { "entities": [{ "text": "Apple Inc.", "label": "ORG" }], "tokens": [...], "noun_chunks": [...], "sentences": [...] } }` },
  ]},
  { category: 'Qdrant (Vector DB) - /api/v1/vectors/*', items: [
    { method: 'POST', path: '/api/v1/vectors/collections', service: 'qdrant', desc: 'Create a vector collection',
      body: `{ "collection_name": "my_docs", "vector_size": 1024, "distance": "cosine" }`, response: `{ "status": "ok", "collection_name": "my_docs" }` },
    { method: 'GET', path: '/api/v1/vectors/collections', service: 'qdrant', desc: 'List all collections', body: `(no body)`, response: `{ "collections": [{ "name": "my_docs", "vectors_count": 1500 }] }` },
    { method: 'POST', path: '/api/v1/vectors/upsert', service: 'qdrant', desc: 'Store vectors with payload',
      body: `{ "collection_name": "my_docs", "points": [{ "id": "550e8400-e29b-41d4-a716-446655440000", "vector": [0.023, ...], "payload": { "text": "..." } }] }`, response: `{ "status": "ok", "upserted": 1 }` },
    { method: 'POST', path: '/api/v1/vectors/search', service: 'qdrant', desc: 'Semantic similarity search',
      body: `{ "collection_name": "my_docs", "vector": [0.023, ...], "limit": 10, "filter": { "must": [{ "key": "source", "match": { "value": "upload" } }] } }`,
      response: `{ "results": [{ "id": "550e8400-...", "score": 0.95, "payload": {...} }] }` },
    { method: 'POST', path: '/api/v1/vectors/delete', service: 'qdrant', desc: 'Delete points by IDs',
      body: `{ "collection_name": "my_docs", "point_ids": ["550e8400-e29b-41d4-a716-446655440000"] }`, response: `{ "status": "ok", "deleted": 1 }` },
  ]},
  { category: 'Elasticsearch (Search) - /api/v1/search/*', items: [
    { method: 'POST', path: '/api/v1/search/indices', service: 'elasticsearch', desc: 'Create a search index with mappings',
      body: `{ "index_name": "my_search", "mappings": { "properties": { "content": { "type": "text" }, "title": { "type": "text" } } } }`, response: `{ "status": "ok", "acknowledged": true }` },
    { method: 'POST', path: '/api/v1/search/index', service: 'elasticsearch', desc: 'Index (store) a document',
      body: `{ "index_name": "my_search", "doc_id": "doc_001", "document": { "content": "ML is...", "title": "Intro" } }`, response: `{ "result": "created", "_id": "doc_001" }` },
    { method: 'POST', path: '/api/v1/search/query', service: 'elasticsearch', desc: 'Full-text search with BM25',
      body: `{ "index_name": "my_search", "query": { "multi_match": { "query": "machine learning", "fields": ["content", "title"] } }, "size": 10 }`,
      response: `{ "hits": { "total": { "value": 42 }, "hits": [{ "_score": 12.5, "_source": {...} }] } }` },
  ]},
  { category: 'Neo4j (Graph) - /api/v1/graph/*', items: [
    { method: 'POST', path: '/api/v1/graph/query', service: 'neo4j', desc: 'Execute Cypher query with parameters',
      body: `{ "cypher": "MATCH (n:Person {name: $name}) RETURN n", "parameters": { "name": "John" }, "database": "neo4j" }`,
      response: `{ "records": [{ "n": { "name": "John", "age": 30 } }], "summary": { "nodes_created": 0 } }` },
  ]},
  { category: 'Redis (Cache) - /api/v1/cache/*', items: [
    { method: 'POST', path: '/api/v1/cache/set', service: 'redis', desc: 'Set a key with optional TTL',
      body: `{ "key": "session:user_001", "value": { "logged_in": true }, "ttl_seconds": 3600 }`, response: `{ "status": "OK", "key": "session:user_001" }` },
    { method: 'POST', path: '/api/v1/cache/get', service: 'redis', desc: 'Get value by key',
      body: `{ "key": "session:user_001" }`, response: `{ "key": "session:user_001", "value": {...}, "exists": true }` },
    { method: 'POST', path: '/api/v1/cache/delete', service: 'redis', desc: 'Delete a key', body: `{ "key": "session:user_001" }`, response: `{ "status": "OK", "deleted": 1 }` },
    { method: 'POST', path: '/api/v1/cache/keys', service: 'redis', desc: 'List keys by pattern', body: `{ "pattern": "session:*" }`, response: `{ "keys": ["session:user_001", ...] }` },
    { method: 'POST', path: '/api/v1/cache/publish', service: 'redis', desc: 'Publish to a Pub/Sub channel',
      body: `{ "channel": "notifications", "message": { "event": "update" } }`, response: `{ "status": "OK", "subscribers": 3 }` },
  ]},
  { category: 'Kafka (Streaming) - /api/v1/streaming/*', items: [
    { method: 'POST', path: '/api/v1/streaming/produce', service: 'kafka', desc: 'Produce a message to a topic',
      body: `{ "topic": "events", "key": "doc_001", "value": { "event": "uploaded", "payload": {...} } }`, response: `{ "status": "ok", "partition": 0, "offset": 42 }` },
    { method: 'POST', path: '/api/v1/streaming/consume', service: 'kafka', desc: 'Consume messages from a topic',
      body: `{ "topic": "events", "group_id": "consumer_1", "max_messages": 10, "timeout_ms": 5000 }`,
      response: `{ "messages": [{ "key": "doc_001", "value": {...}, "partition": 0, "offset": 42 }] }` },
    { method: 'POST', path: '/api/v1/streaming/topics', service: 'kafka', desc: 'Create a topic', body: `{ "topic": "events", "num_partitions": 3 }`, response: `{ "status": "ok" }` },
    { method: 'GET', path: '/api/v1/streaming/topics', service: 'kafka', desc: 'List topics', body: `(no body)`, response: `{ "topics": ["events", "logs"] }` },
  ]},
  { category: 'MinIO (S3 Storage) - /api/v1/storage/*', note: 'All bucket names are automatically prefixed with your client ID (e.g., "my-documents" becomes "client-{your_id}-my-documents" internally). This means your files are fully isolated — no other customer can access your storage. You just use simple names like "my-documents" in all requests.', items: [
    { method: 'POST', path: '/api/v1/storage/buckets', service: 'minio', desc: 'Create a bucket', body: `{ "bucket_name": "my-documents" }`, response: `{ "status": "ok", "bucket": "my-documents" }` },
    { method: 'GET', path: '/api/v1/storage/buckets', service: 'minio', desc: 'List buckets', body: `(no body)`, response: `{ "buckets": [{ "name": "my-documents" }] }` },
    { method: 'POST', path: '/api/v1/storage/upload', service: 'minio', desc: 'Upload a file directly (multipart form data)',
      body: `Content-Type: multipart/form-data\n\nbucket_name: "my-documents"\nobject_name: "reports/report.pdf"\nfile: <binary file>`,
      response: `{ "status": "uploaded", "bucket_name": "my-documents", "object_name": "reports/report.pdf", "size_bytes": 102400, "content_type": "application/pdf" }` },
    { method: 'GET', path: '/api/v1/storage/download/{bucket}/{path}', service: 'minio', desc: 'Download a file (returns binary stream)',
      body: `(no body — bucket and object path in URL)\nExample: GET /api/v1/storage/download/my-documents/reports/report.pdf`,
      response: `Binary file content\nHeaders: Content-Disposition: attachment; filename="report.pdf"` },
    { method: 'POST', path: '/api/v1/storage/list', service: 'minio', desc: 'List objects in a bucket',
      body: `{ "bucket_name": "my-documents", "prefix": "reports/", "max_keys": 100 }`, response: `{ "objects": [{ "object_name": "report.pdf", "size_bytes": 1024000 }] }` },
    { method: 'POST', path: '/api/v1/storage/presigned', service: 'minio', desc: 'Generate presigned URL (for internal service-to-service use)',
      body: `{ "bucket_name": "my-documents", "object_name": "report.pdf", "method": "GET", "expires_seconds": 3600 }`, response: `{ "url": "http://minio:9000/...", "expires_at": "..." }\nNote: URL uses internal hostname — use /storage/upload and /storage/download for external access` },
  ]},
  { category: 'AI Search (Vane + SearXNG) - /api/v1/search/*', items: [
    { method: 'POST', path: '/api/v1/search/ai-query', service: 'vane_search', desc: 'AI-powered search — returns a researched answer with cited sources',
      body: `{ "query": "What are AI regulations in California?", "mode": "balanced", "domain": "law.com" }`,
      response: `{ "answer": "Based on multiple sources...", "sources": [{ "title": "...", "url": "...", "snippet": "..." }] }` },
    { method: 'POST', path: '/api/v1/search/raw', service: 'searxng', desc: 'Raw web search — results from Google, Bing, DuckDuckGo and more',
      body: `{ "query": "machine learning papers 2026", "limit": 10, "domain": "arxiv.org" }`,
      response: `{ "results": [{ "title": "...", "url": "...", "snippet": "..." }], "query": "..." }` },
  ]},
  { category: 'Headless Browser (Lightpanda) - /api/v1/browse/*', items: [
    { method: 'POST', path: '/api/v1/browse/render', service: 'lightpanda_browser', desc: 'Render a web page and return full content (handles JavaScript)',
      body: `{ "url": "https://example.com/pricing", "wait_for": "networkidle", "timeout_ms": 30000 }`,
      response: `{ "html": "...", "text": "extracted plain text", "title": "page title", "status_code": 200 }` },
    { method: 'POST', path: '/api/v1/browse/extract', service: 'lightpanda_browser', desc: 'Render page and extract specific content using CSS selectors',
      body: `{ "url": "https://example.com/article", "selectors": { "title": "h1", "content": "article", "date": "time" }, "timeout_ms": 30000 }`,
      response: `{ "extracted": { "title": "...", "content": "...", "date": "..." }, "url": "..." }` },
  ]},
  { category: 'PDF Parser - /api/v1/pdf/*', items: [
    { method: 'POST', path: '/api/v1/pdf/parse', service: 'pdf_parser', desc: 'Parse PDF — extract text, metadata, and pages (fast, native PDFs)',
      body: `{ "url": "https://example.com/document.pdf" }`,
      response: `{ "parser": "pymupdf", "page_count": 3, "metadata": { "title": "...", "author": "..." }, "full_text": "...", "pages": [{ "page_number": 1, "text": "..." }] }` },
    { method: 'POST', path: '/api/v1/pdf/parse-ocr', service: 'pdf_parser', desc: 'Parse scanned PDF with OCR (slower, handles images)',
      body: `{ "url": "https://example.com/scanned-doc.pdf" }`,
      response: `{ "parser": "unstructured", "element_count": 5, "full_text": "...", "elements": [{ "type": "Title", "text": "..." }] }` },
    { method: 'POST', path: '/api/v1/pdf/extract-tables', service: 'pdf_parser', desc: 'Extract tables from PDF as structured data',
      body: `{ "url": "https://example.com/report.pdf" }`,
      response: `{ "elements": [{ "type": "Table", "text": "...", "html_table": "<table>...</table>" }] }` },
  ]},
  { category: 'Docling (Universal Document AI) - /api/v1/docs/*', note: 'Supports 20+ formats: PDF, DOCX, PPTX, XLSX, HTML, CSV, Markdown, LaTeX, images, audio, video, XBRL, JATS, USPTO, WebVTT. Returns structured JSON with confidence scores, bounding boxes, tables, and images. All resources are auto-namespaced per customer. All Docling endpoints are async — they return HTTP 202 with a job_id. See the Async Jobs section below.', items: [
    { method: 'POST', path: '/api/v1/docs/convert', service: 'docling', desc: 'Convert any document — PDF, DOCX, PPTX, XLSX, HTML, images, and more (async — returns 202)',
      body: `{ "url": "presigned or public URL", "output_format": "json"|"markdown"|"html"|"text"|"doctags", "max_pages": 100 }`,
      response: `HTTP 202\n{ "job_id": "job_a1b2c3...", "status": "pending", "poll_url": "/api/v1/jobs/job_a1b2c3...", "message": "Job accepted. Poll the poll_url for status and results." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/convert-ocr', service: 'docling', desc: 'Convert with forced OCR — for scanned PDFs and images (async — returns 202)',
      body: `{ "url": "presigned or public URL", "output_format": "json" }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/chunk', service: 'docling', desc: 'Convert + chunk for RAG — token-aware, includes heading context per chunk (async — returns 202)',
      body: `{ "url": "presigned or public URL", "max_tokens": 512, "merge_peers": true }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/convert-vlm', service: 'docling', desc: 'Convert using Vision Language Model — best for charts, diagrams, complex layouts (async — returns 202)',
      body: `{ "url": "presigned or public URL", "output_format": "markdown" }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/transcribe', service: 'docling', desc: 'Transcribe audio/video — WAV, MP3, M4A, AAC, OGG, FLAC, MP4, AVI, MOV (async — returns 202)',
      body: `{ "url": "presigned or public URL", "output_format": "markdown" }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/transcribe-diarized', service: 'docling', desc: 'Transcribe with speaker identification (async — returns 202)',
      body: `{ "url": "presigned or public URL" }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
    { method: 'POST', path: '/api/v1/docs/extract-metadata', service: 'docling', desc: 'Extract format-specific metadata — PDF bookmarks/links/forms, PPTX notes, DOCX changes, HTML (async — returns 202)',
      body: `{ "url": "presigned or public URL" }`,
      response: `HTTP 202\n{ "job_id": "job_...", "status": "pending", "poll_url": "/api/v1/jobs/job_...", "message": "Job accepted..." }\nSee Async Jobs section for polling and lifecycle details.` },
  ]},
];

export default function Docs() {
  const [svcData, setSvcData] = useState({});
  useEffect(() => { svcApi.list().then(r => setSvcData(r.data.services || {})).catch(() => {}); }, []);

  const pythonFull = `import requests
import uuid

BASE = "${API_BASE}/api/v1"
KEY = "gs_live_your_key_here"
H = {"X-API-Key": KEY, "Content-Type": "application/json"}

# ── Embeddings (BGE-M3: 1024d, MiniLM: 384d) ──
r = requests.post(f"{BASE}/embeddings/embed", headers=H, json={
    "texts": ["What is ML?", "Deep learning"],
    "model": "bge_m3", "return_sparse": True
})
dense_vectors = r.json()["data"]["dense"]

# ── Rerank passages ──
r = requests.post(f"{BASE}/embeddings/rerank", headers=H, json={
    "query": "neural networks",
    "passages": ["Neural nets...", "Cooking..."], "top_k": 1
})

# ── NLP (NER, POS, dependencies) ──
r = requests.post(f"{BASE}/embeddings/nlp", headers=H, json={
    "text": "Apple Inc. is in Cupertino."
})

# ── Qdrant: Store and search vectors ──
requests.post(f"{BASE}/vectors/collections", headers=H, json={
    "collection_name": "my_docs", "vector_size": 1024
})
requests.post(f"{BASE}/vectors/upsert", headers=H, json={
    "collection_name": "my_docs",
    # id must be UUID string or unsigned int (both OK)
    "points": [{"id": str(uuid.uuid4()), "vector": dense_vectors[0], "payload": {"text": "..."}}]
})
results = requests.post(f"{BASE}/vectors/search", headers=H, json={
    "collection_name": "my_docs", "vector": dense_vectors[0], "limit": 5
}).json()

# ── Elasticsearch: Full-text search ──
requests.post(f"{BASE}/search/indices", headers=H, json={
    "index_name": "my_search", "mappings": {"properties": {"content": {"type": "text"}}}
})
requests.post(f"{BASE}/search/index", headers=H, json={
    "index_name": "my_search", "doc_id": "doc_1", "document": {"content": "ML intro"}
})
hits = requests.post(f"{BASE}/search/query", headers=H, json={
    "index_name": "my_search", "query": {"match": {"content": "ML"}}, "size": 5
}).json()

# ── Neo4j: Graph queries ──
requests.post(f"{BASE}/graph/query", headers=H, json={
    "cypher": "CREATE (n:Doc {id: $id, title: $t}) RETURN n",
    "parameters": {"id": "doc_1", "t": "ML Intro"}
})

# ── Redis: Cache ──
requests.post(f"{BASE}/cache/set", headers=H, json={
    "key": "session:123", "value": {"active": True}, "ttl_seconds": 3600
})
cached = requests.post(f"{BASE}/cache/get", headers=H, json={"key": "session:123"}).json()

# ── Kafka: Event streaming ──
requests.post(f"{BASE}/streaming/produce", headers=H, json={
    "topic": "events", "key": "doc_1", "value": {"event": "processed"}
})
msgs = requests.post(f"{BASE}/streaming/consume", headers=H, json={
    "topic": "events", "group_id": "my_consumer", "max_messages": 10
}).json()

# ── MinIO: Object storage ──
requests.post(f"{BASE}/storage/buckets", headers=H, json={"bucket_name": "my-data"})

# Upload a file directly (no presigned URL needed)
with open("report.pdf", "rb") as f:
    requests.post(f"{BASE}/storage/upload",
        headers={"X-API-Key": KEY},
        data={"bucket_name": "my-data", "object_name": "reports/report.pdf"},
        files={"file": ("report.pdf", f, "application/pdf")}
    )

# Download a file
resp = requests.get(f"{BASE}/storage/download/my-data/reports/report.pdf", headers=H)
with open("downloaded.pdf", "wb") as f:
    f.write(resp.content)

# List objects
objects = requests.post(f"{BASE}/storage/list", headers=H, json={
    "bucket_name": "my-data", "prefix": ""
}).json()

# ── AI Search (Vane) — researched answer with citations ──
answer = requests.post(f"{BASE}/search/ai-query", headers=H, json={
    "query": "What are the latest AI regulations?", "mode": "balanced"
}).json()

# ── Raw Web Search (SearXNG) — aggregated results ──
results = requests.post(f"{BASE}/search/raw", headers=H, json={
    "query": "machine learning papers 2026", "limit": 10
}).json()

# ── Headless Browser (Lightpanda) — render JS pages ──
page = requests.post(f"{BASE}/browse/render", headers=H, json={
    "url": "https://example.com", "timeout_ms": 30000
}).json()
extracted = requests.post(f"{BASE}/browse/extract", headers=H, json={
    "url": "https://example.com",
    "selectors": {"title": "h1", "content": "article"}
}).json()

# ── PDF Parser — extract text, tables, OCR ──
doc = requests.post(f"{BASE}/pdf/parse", headers=H, json={
    "url": "https://example.com/document.pdf"
}).json()
doc_ocr = requests.post(f"{BASE}/pdf/parse-ocr", headers=H, json={
    "url": "https://example.com/scanned.pdf"
}).json()
tables = requests.post(f"{BASE}/pdf/extract-tables", headers=H, json={
    "url": "https://example.com/report.pdf"
}).json()

# ── Docling (Universal Document AI) — convert any document ──
# Convert a DOCX to markdown
doc = requests.post(f"{BASE}/docs/convert", headers=H, json={
    "url": "https://example.com/report.docx", "output_format": "markdown"
}).json()
print(doc["content"])  # Clean markdown

# OCR a scanned image
ocr = requests.post(f"{BASE}/docs/convert-ocr", headers=H, json={
    "url": "https://example.com/scanned.png"
}).json()

# Chunk a PDF for RAG (token-aware)
chunks = requests.post(f"{BASE}/docs/chunk", headers=H, json={
    "url": "https://example.com/paper.pdf", "max_tokens": 512
}).json()
for c in chunks["chunks"]:
    print(f"Chunk {c['index']}: {c['text'][:80]}...")

# Convert with VLM (complex charts/diagrams)
vlm = requests.post(f"{BASE}/docs/convert-vlm", headers=H, json={
    "url": "https://example.com/chart-report.pdf", "output_format": "markdown"
}).json()

# Transcribe audio/video
transcript = requests.post(f"{BASE}/docs/transcribe", headers=H, json={
    "url": "https://example.com/meeting.mp3", "output_format": "markdown"
}).json()
print(transcript["content"])  # Timestamped transcript

# Transcribe with speaker diarization (who said what)
diarized = requests.post(f"{BASE}/docs/transcribe-diarized", headers=H, json={
    "url": "https://example.com/meeting.mp3"
}).json()
print(f"Speakers: {diarized['num_speakers']}, Language: {diarized['language']}")
for seg in diarized["segments"]:
    print(f"  [{seg['speaker']}] {seg['start']:.1f}s-{seg['end']:.1f}s: {seg['text']}")

# Extract format-specific metadata (PDF bookmarks, HTML links, PPTX notes)
meta = requests.post(f"{BASE}/docs/extract-metadata", headers=H, json={
    "url": "https://example.com/document.pdf"
}).json()
print(f"Format: {meta['format']}, Bookmarks: {len(meta.get('bookmarks',[]))}, Links: {len(meta.get('links',[]))}")`;

  return (
    <div data-testid="docs-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">API Documentation</h2>
        <p className="text-muted-foreground mt-1">Complete reference for all 15 GPU-powered services</p>
      </div>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Authentication</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>All requests require <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">X-API-Key</code> header with your <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">gs_live_</code> key.</p>
          <p>You must <strong>enable each service</strong> in the dashboard before you can call its endpoints. Calling a service you haven't enabled returns 403.</p>
          <p><strong>Data isolation:</strong> All resource names (buckets, collections, indices, keys, topics) are automatically prefixed with your client ID. You just use simple names like <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs">"my-documents"</code> — the platform handles isolation. No other customer can access your data.</p>
        </CardContent>
      </Card>

      <Card id="rate-limits" className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Rate Limits & Quotas</CardTitle></CardHeader>
        <CardContent className="space-y-5 text-sm text-muted-foreground">
          <p>The platform enforces three layers of limits to protect availability and ensure fair resource usage. Most legitimate workloads never hit these limits.</p>

          <div>
            <p className="font-medium text-foreground mb-2">Three Layers of Protection</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-border/30"><th className="text-left py-2 px-3 text-foreground">Layer</th><th className="text-left py-2 px-3 text-foreground">Purpose</th><th className="text-left py-2 px-3 text-foreground">Where enforced</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Edge (per-IP)</td><td className="py-2 px-3">Prevent abuse and DDoS</td><td className="py-2 px-3">nginx, before requests reach the application</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Account (per-user)</td><td className="py-2 px-3">Fair share of platform resources</td><td className="py-2 px-3">Application layer</td></tr>
                  <tr><td className="py-2 px-3">Resource (per-request)</td><td className="py-2 px-3">Prevent oversized payloads</td><td className="py-2 px-3">Application + nginx</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Edge Rate Limiting (per IP)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-border/30"><th className="text-left py-2 px-3 text-foreground">Setting</th><th className="text-left py-2 px-3 text-foreground">Value</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Average rate</td><td className="py-2 px-3 font-mono">100 requests/second per IP</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Burst tolerance</td><td className="py-2 px-3 font-mono">200 requests</td></tr>
                  <tr><td className="py-2 px-3">Response when exceeded</td><td className="py-2 px-3">HTTP 503 with empty body</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Applied at the nginx layer before any authentication. If you see 503s consistently, implement exponential backoff (see example below).</p>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Account-Level Limits</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-border/30"><th className="text-left py-2 px-3 text-foreground">Setting</th><th className="text-left py-2 px-3 text-foreground">Value</th><th className="text-left py-2 px-3 text-foreground">Response</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Concurrent async jobs per account</td><td className="py-2 px-3 font-mono">10</td><td className="py-2 px-3">HTTP 429</td></tr>
                  <tr><td className="py-2 px-3">Maximum upload size per request</td><td className="py-2 px-3 font-mono">25 GB</td><td className="py-2 px-3">HTTP 413</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Concurrent job limit applies across all async endpoints (Docling conversion, OCR, chunking, VLM, transcription). Higher limits available on request.</p>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Async Job Retry Policy</p>
            <p className="text-xs mb-2">When you submit an async job, retries are handled by the platform automatically if our system is temporarily saturated. You'll only see a final failure after all retries are exhausted.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-border/30"><th className="text-left py-2 px-3 text-foreground">Failure type</th><th className="text-left py-2 px-3 text-foreground">Retry schedule</th><th className="text-left py-2 px-3 text-foreground">Total attempts</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Service saturated (503)</td><td className="py-2 px-3 font-mono">5s → 15s → 30s → 60s → 120s</td><td className="py-2 px-3">5</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3">Transient error</td><td className="py-2 px-3 font-mono">30s → 60s → 120s</td><td className="py-2 px-3">3</td></tr>
                  <tr><td className="py-2 px-3">Hard failure (4xx)</td><td className="py-2 px-3">Not retried</td><td className="py-2 px-3">1</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Webhook Delivery Retries</p>
            <p className="text-xs mb-2">If your webhook endpoint is unreachable, we retry on this schedule:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="border-b border-border/30"><th className="text-left py-2 px-3 text-foreground">Attempt</th><th className="text-left py-2 px-3 text-foreground">Delay after previous failure</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/10"><td className="py-2 px-3 font-mono">1</td><td className="py-2 px-3">30 seconds</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3 font-mono">2</td><td className="py-2 px-3">2 minutes</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3 font-mono">3</td><td className="py-2 px-3">10 minutes</td></tr>
                  <tr className="border-b border-border/10"><td className="py-2 px-3 font-mono">4</td><td className="py-2 px-3">1 hour</td></tr>
                  <tr><td className="py-2 px-3 font-mono">5</td><td className="py-2 px-3">4 hours</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">After 20 consecutive failed deliveries, the webhook is automatically deactivated. Reactivate via <code className="text-primary bg-primary/10 px-1 rounded">PUT /api/v1/webhooks/{'{'}id{'}'}</code>.</p>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Best Practices for Clients</p>
            <div className="space-y-1.5 text-xs">
              <p><strong>Honor 503 responses.</strong> A 503 means we're temporarily protecting capacity. Wait briefly and retry — don't hammer.</p>
              <p><strong>Implement exponential backoff with jitter.</strong> Don't retry all failed requests at the same interval; spread them out.</p>
              <p><strong>Don't retry 4xx errors.</strong> Those indicate a problem with your request (bad API key, malformed body, etc.) — retrying won't help.</p>
              <p><strong>Track your async job count.</strong> You can have 10 jobs in flight. If you need more concurrency, queue them client-side instead of submitting all at once.</p>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Example: Proper Retry Handling in Python</p>
            <CodeBlock code={`import time, random, requests

def call_with_retries(url, headers, json_body, max_retries=5):
    for attempt in range(max_retries):
        resp = requests.post(url, headers=headers, json=json_body, timeout=30)

        # Success
        if resp.status_code < 400:
            return resp

        # Rate limited / saturated — back off and retry
        if resp.status_code in (429, 503):
            delay = (2 ** attempt) + random.uniform(0, 1)
            time.sleep(min(delay, 120))
            continue

        # 4xx other than 429 — bug in our request, don't retry
        resp.raise_for_status()

    raise Exception("Max retries exceeded")`} />
          </div>

          <div className="p-3 rounded-lg bg-primary/5 border border-primary/10 text-xs">
            <p className="font-bold text-foreground mb-1">Requesting Higher Limits</p>
            <p>If your use case requires higher concurrent job limits or different rate limits, contact <a href="mailto:support@tensornex.cloud" className="text-primary underline">support@tensornex.cloud</a> with: your account email, use case description, expected request volume (per second / per day), and job concurrency you need. We'll typically respond within one business day.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Error Handling</CardTitle></CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <div>
            <p className="font-medium text-foreground mb-2">Validation Errors (400) — Your input needs fixing</p>
            <div className="space-y-1.5 text-xs">
              <p><code className="text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">400</code> <strong>Unsupported file type</strong> — You sent a file format we don't support (e.g., .exe). The error message lists all supported formats.</p>
              <p><code className="text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">400</code> <strong>No URL provided</strong> — The 'url' field is empty. Upload your file to <code className="text-primary bg-primary/10 px-1 rounded">/storage/upload</code> first, then use the presigned URL.</p>
              <p><code className="text-destructive bg-destructive/10 px-1.5 py-0.5 rounded">400</code> <strong>Invalid JSON</strong> — Check your request body format.</p>
            </div>
          </div>
          <div>
            <p className="font-medium text-foreground mb-2">Auth & Subscription Errors</p>
            <div className="space-y-1.5 text-xs">
              <p><code className="text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">401</code> Missing or invalid API key.</p>
              <p><code className="text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">402</code> Insufficient wallet balance. Add credit.</p>
              <p><code className="text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">403</code> Service not in your subscription. Subscribe first.</p>
              <p><code className="text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">429</code> Daily rate limit reached. Wait or upgrade tier.</p>
            </div>
          </div>
          <div>
            <p className="font-medium text-foreground mb-2">Processing Errors (502/504) — Tracked automatically</p>
            <p className="text-xs mb-2">When a request fails during processing, you receive a structured response with a <strong>reference ID</strong>. Our team is notified automatically.</p>
            <CodeBlock code={`// Error response format:
{
  "detail": {
    "error": "processing_failed",
    "message": "Your request could not be processed. Our team has been notified.",
    "reference_id": "ERR-20260425-A3X7",
    "suggestions": [
      "Try a different file or format",
      "Try again in a few minutes",
      "Contact support with reference ID ERR-20260425-A3X7"
    ]
  }
}

// How to handle in Python:
resp = requests.post(f"{BASE}/docs/convert", headers=H, json=payload)
if resp.status_code in (502, 504):
    detail = resp.json().get("detail", {})
    ref_id = detail.get("reference_id", "unknown") if isinstance(detail, dict) else "unknown"
    print(f"Failed (ref: {ref_id}). Contact support with this ID.")
elif resp.status_code == 400:
    print(f"Input error: {resp.json()['detail']}")  # Fix your input and retry`} />
          </div>
        </CardContent>
      </Card>

      {endpoints.map(cat => (
        <div key={cat.category} className="space-y-3">
          <h3 className="text-base font-bold flex items-center gap-2 mt-6">
            <Badge variant="outline" className="text-xs font-medium">{cat.category}</Badge>
          </h3>
          {cat.note && (
            <p className="text-xs text-muted-foreground p-3 rounded-lg bg-secondary/20 border border-border/20">{cat.note}</p>
          )}
          {cat.items.map(ep => (
            <Card key={ep.path + ep.method} className="bg-card border-border/50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`font-mono text-xs ${ep.method === 'GET' ? 'bg-blue-500/20 text-blue-400' : ep.method === 'DELETE' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>{ep.method}</Badge>
                  <code className="text-sm font-mono font-medium">{ep.path}</code>
                  <span className="text-[11px] text-muted-foreground ml-auto">Requires: <span className="text-primary">{ep.service}</span></span>
                </div>
                <p className="text-sm text-muted-foreground">{ep.desc}</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  <div><p className="text-[11px] text-muted-foreground mb-1">Request</p><CodeBlock code={ep.body} /></div>
                  <div><p className="text-[11px] text-muted-foreground mb-1">Response</p><CodeBlock code={ep.response} /></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ))}

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Full Python Example</CardTitle></CardHeader>
        <CardContent><CodeBlock code={pythonFull} /></CardContent>
      </Card>

      <Card id="webhooks" className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Webhook Notifications</CardTitle></CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>Receive real-time HTTP POST notifications when your async jobs complete, fail, or are cancelled. Eliminates the need to poll <code className="text-primary bg-primary/10 px-1 rounded">/api/v1/jobs/{'{'}_id{'}'}</code> repeatedly.</p>

          <div>
            <p className="font-medium text-foreground mb-2">Register a Webhook</p>
            <CodeBlock code={`# Register a webhook — secret is shown ONCE, save it
resp = requests.post(f"{BASE}/webhooks", headers=H, json={
    "url": "https://your-server.com/tensornex-webhook",
    "events": ["job.completed", "job.failed", "job.cancelled"]
})
webhook = resp.json()
# {"id": "664a...", "secret": "whsec_a1b2c3...", "url": "...", "events": [...]}
# Save webhook['secret'] — it will not be shown again`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Payload We Send</p>
            <CodeBlock code={`{
  "event": "job.completed",
  "timestamp": "2026-05-11T14:30:00Z",
  "delivery_id": "wd_a1b2c3d4...",
  "data": {
    "job_id": "job_a1b2c3...",
    "service": "docling",
    "endpoint": "/internal/docling/convert",
    "status": "completed",
    "created_at": "...", "started_at": "...", "completed_at": "...",
    "actual_cost": 0.10,
    "result": { ... }   // For completed jobs
    // "error": { ... }  // For failed jobs
    // "refunded": 0.10  // For failed/cancelled jobs
  }
}`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Headers We Send</p>
            <div className="text-xs space-y-1 font-mono bg-background p-3 rounded-lg border border-border/30">
              <p>Content-Type: application/json</p>
              <p>User-Agent: Tensornex-Webhooks/1.0</p>
              <p>X-Tensornex-Signature: sha256={'<'}hex-encoded HMAC-SHA256{'>'}</p>
              <p>X-Tensornex-Delivery: wd_a1b2c3d4...</p>
              <p>X-Tensornex-Event: job.completed</p>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Verify the Signature (Python)</p>
            <CodeBlock code={`import hmac, hashlib

def verify_signature(payload_bytes: bytes, secret: str, signature_header: str) -> bool:
    expected = "sha256=" + hmac.new(
        secret.encode("utf-8"), payload_bytes, hashlib.sha256
    ).hexdigest()
    return hmac.compare_digest(expected, signature_header)

# In your webhook handler:
# payload_bytes = request.body  (raw bytes)
# sig = request.headers["X-Tensornex-Signature"]
# is_valid = verify_signature(payload_bytes, your_webhook_secret, sig)`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Verify the Signature (Node.js)</p>
            <CodeBlock code={`const crypto = require('crypto');

function verifySignature(payloadBuffer, secret, signatureHeader) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(payloadBuffer)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(expected), Buffer.from(signatureHeader)
  );
}`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Retry Behavior</p>
            <div className="space-y-1.5 text-xs">
              <p>If your endpoint doesn't return 2xx, we retry up to <strong>5 times</strong> with backoff: 30s → 2min → 10min → 1h → 4h.</p>
              <p>After <strong>20 consecutive failures</strong> across deliveries, the webhook is automatically deactivated. Re-activate by updating it via the API.</p>
              <p><strong>Best practice:</strong> Return 200 immediately, process the payload asynchronously on your side. Webhook delivery times out after 10 seconds.</p>
              <p><strong>Idempotency:</strong> Handle duplicate deliveries — we may retry even on successful delivery if our network response is lost. Use <code className="text-primary bg-primary/10 px-1 rounded">delivery_id</code> as a deduplication key.</p>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Management Endpoints</p>
            <div className="space-y-1 text-xs">
              <p><code className="text-primary bg-primary/10 px-1 rounded">POST /api/v1/webhooks</code> — Register a new webhook (returns secret once)</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">GET /api/v1/webhooks</code> — List your webhooks (without secrets)</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">GET /api/v1/webhooks/{'{'}id{'}'}/deliveries</code> — Recent delivery attempts with status codes</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">PUT /api/v1/webhooks/{'{'}id{'}'}</code> — Update URL or events</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">POST /api/v1/webhooks/{'{'}id{'}'}/rotate-secret</code> — Generate a new secret (old one invalidated)</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">DELETE /api/v1/webhooks/{'{'}id{'}'}</code> — Delete a webhook</p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs">
            <p className="font-bold text-amber-400 mb-1">URL Requirements</p>
            <p>Webhook URLs must use <strong>HTTPS</strong> and must not resolve to private/internal IP addresses (10.x, 172.16-31.x, 192.168.x, 127.x). This prevents SSRF attacks.</p>
          </div>
        </CardContent>
      </Card>

      <Card id="async-jobs" className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Async Jobs</CardTitle></CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>All Docling endpoints (<code className="text-primary bg-primary/10 px-1 rounded">/docs/*</code>) are <strong>asynchronous</strong>. Instead of waiting minutes for a result, you receive an immediate <code className="text-primary bg-primary/10 px-1 rounded">HTTP 202</code> response with a <code className="text-primary bg-primary/10 px-1 rounded">job_id</code>. Poll the job endpoint for status and results, or <a href="#webhooks" className="text-primary underline">register a webhook</a> to be notified automatically.</p>

          <div>
            <p className="font-medium text-foreground mb-2">Job Lifecycle</p>
            <div className="flex items-center gap-2 text-xs font-mono p-3 bg-background rounded-lg border border-border/30 flex-wrap">
              <Badge className="bg-amber-500/20 text-amber-400">pending</Badge><span>→</span>
              <Badge className="bg-blue-500/20 text-blue-400">running</Badge><span>→</span>
              <Badge className="bg-emerald-500/20 text-emerald-400">completed</Badge>
              <span className="text-muted-foreground mx-2">or</span>
              <Badge className="bg-red-500/20 text-red-400">failed</Badge>
              <span className="text-muted-foreground mx-2">or</span>
              <Badge className="bg-slate-500/20 text-slate-400">cancelled</Badge>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Submit a Job (any Docling endpoint)</p>
            <CodeBlock code={`resp = requests.post(f"{BASE}/docs/convert", headers=H, json={
    "url": presigned_url, "output_format": "markdown"
})
# HTTP 202
job = resp.json()
# {"job_id": "job_a1b2c3...", "status": "pending", "poll_url": "/api/v1/jobs/job_a1b2c3..."}`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Poll for Results</p>
            <CodeBlock code={`import time

while True:
    job = requests.get(f"{BASE}/jobs/{job_id}", headers=H).json()
    print(f"Status: {job['status']}")
    
    if job["status"] == "completed":
        result = job["result"]  # The actual conversion result
        break
    elif job["status"] == "failed":
        print(f"Error: {job['error']}")
        break
    elif job["status"] == "cancelled":
        print("Job was cancelled")
        break
    
    time.sleep(3)  # Poll every 3 seconds`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Job Endpoints</p>
            <div className="space-y-1 text-xs">
              <p><code className="text-primary bg-primary/10 px-1 rounded">GET /api/v1/jobs/{'{'}job_id{'}'}</code> — Get job status + result (auth: same API key that created it)</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">GET /api/v1/jobs?status=pending&service=docling&page=1&per_page=20</code> — List your jobs (paginated, filterable)</p>
              <p><code className="text-primary bg-primary/10 px-1 rounded">DELETE /api/v1/jobs/{'{'}job_id{'}'}</code> — Cancel a pending/running job (refunds reserved cost)</p>
            </div>
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Cost Behavior</p>
            <div className="space-y-1.5 text-xs">
              <p><strong>On submit:</strong> Estimated cost is reserved (deducted from wallet immediately). This prevents over-submission.</p>
              <p><strong>On completion:</strong> Reserved cost is reconciled with actual usage.</p>
              <p><strong>On failure/cancellation:</strong> Reserved cost is refunded to your wallet.</p>
            </div>
          </div>

          <div className="p-3 rounded-lg bg-secondary/10 border border-border/20 text-xs space-y-1">
            <p><strong>Concurrency limit:</strong> 10 concurrent jobs per account by default. Submitting an 11th returns <code className="text-primary bg-primary/10 px-1 rounded">429</code>.</p>
            <p><strong>Webhooks:</strong> If you've <a href="#webhooks" className="text-primary underline">registered a webhook</a>, you don't need to poll — we'll POST when the job reaches a terminal state.</p>
          </div>
        </CardContent>
      </Card>

      <Card id="health-endpoint" className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Platform Health Endpoint</CardTitle></CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p><code className="text-primary bg-primary/10 px-1 rounded">GET /api/v1/health</code> — no authentication required, no rate limit. Cached 10s server-side, safe to poll every 5-10 seconds. Use it in your circuit breakers, dashboards, and retry logic.</p>

          <CodeBlock code={`# No API key needed
resp = requests.get(f"{BASE}/health")
health = resp.json()
# Always returns HTTP 200 — read the "status" field for the actual state

print(f"Status: {health['status']}")  # "healthy", "degraded", or "down"
print(f"GPU: {health['gpu_server']['reachable']}")
print(f"Queue: {health['queue']['pending_jobs']} pending, {health['queue']['running_jobs']} running")`} />

          <div>
            <p className="font-medium text-foreground mb-2">Response Payload</p>
            <CodeBlock code={`{
  "status": "healthy",          // "healthy" | "degraded" | "down"
  "timestamp": "2026-05-11T14:30:00Z",
  "gpu_server": {
    "reachable": true,
    "active_concurrency": 3, "max_concurrency": 8,
    "saturation_level": "low"   // "low" | "medium" | "high"
  },
  "queue": { "pending_jobs": 5, "running_jobs": 3 },
  "database": { "main_pool_reachable": true, "auth_pool_reachable": true },
  "uptime_seconds": 86400,
  "version": "1.0"
}`} />
          </div>

          <div>
            <p className="font-medium text-foreground mb-2">Status Interpretation</p>
            <div className="space-y-1.5 text-xs">
              <p><Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">healthy</Badge> — All systems operational. Proceed normally.</p>
              <p><Badge className="bg-amber-500/20 text-amber-400 text-[10px]">degraded</Badge> — Partial issues (GPU saturated, queue building up, or one DB pool down). Requests still succeed but may be slower. Back off retries.</p>
              <p><Badge className="bg-red-500/20 text-red-400 text-[10px]">down</Badge> — Critical issue. GPU unreachable or queue overloaded. Pause submissions until recovery.</p>
              <p className="text-muted-foreground mt-1">HTTP status is always 200, even when internal probes fail. The <code className="text-primary bg-primary/10 px-1 rounded">status</code> field is the source of truth. This is intentional so circuit breakers can rely on a single signal.</p>
            </div>
          </div>

          <p className="text-xs">Live status page: <a href="/status" className="text-primary underline">/status</a></p>
        </CardContent>
      </Card>

      <Card className="bg-card border-border/50">
        <CardHeader><CardTitle className="text-lg">Route Mapping</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/50 text-muted-foreground"><th className="text-left py-2 px-3">Service</th><th className="text-left py-2 px-3">Client Route</th><th className="text-right py-2 px-3">Economy</th><th className="text-right py-2 px-3">Standard</th><th className="text-right py-2 px-3">Priority</th></tr></thead>
              <tbody>
                {[
                  ['bge_m3', '/api/v1/embeddings/embed'], ['bge_reranker', '/api/v1/embeddings/rerank'], ['minilm', '/api/v1/embeddings/embed'], ['spacy', '/api/v1/embeddings/nlp'],
                  ['qdrant', '/api/v1/vectors/*'], ['elasticsearch', '/api/v1/search/query'], ['neo4j', '/api/v1/graph/*'], ['redis', '/api/v1/cache/*'], ['kafka', '/api/v1/streaming/*'], ['minio', '/api/v1/storage/*'],
                  ['vane_search', '/api/v1/search/ai-query'], ['searxng', '/api/v1/search/raw'], ['lightpanda_browser', '/api/v1/browse/*'],
                  ['pdf_parser', '/api/v1/pdf/*'],
                  ['docling', '/api/v1/docs/*'],
                ].map(([id, route]) => {
                  const svc = svcData[id];
                  return svc ? (
                    <tr key={id} className="border-b border-border/20">
                      <td className="py-2 px-3 font-medium">{svc.name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{route}</td>
                      {['economy', 'standard', 'priority'].map(t => (
                        <td key={t} className="py-2 px-3 text-right font-mono text-xs">${svc.pricing[t]?.hourly_rate}/hr</td>
                      ))}
                    </tr>
                  ) : null;
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
